/**
 * What the screens call things. One place, so a card's state reads the same
 * in the queue, on the board and in the audit log.
 */

import {
  anchorText,
  workWord,
  type CardFull,
  type EmployeeState,
  type LifecycleKind,
  type MembershipRole,
  type SlaStatus,
  type TaskState,
} from "@utuwrk/core";

export const STATE_LABEL: Record<TaskState, string> = {
  not_started: "Waiting",
  unblocked: "Ready",
  in_progress: "In progress",
  blocked: "Blocked",
  completed: "Done",
  skipped: "Skipped",
};

/** The pill class for each state: traffic-light colours, always beside an icon and a word (PRD 10.7). */
export const STATE_CLASS: Record<TaskState, string> = {
  not_started: "s-waiting",
  unblocked: "s-ready",
  in_progress: "s-progress",
  blocked: "s-danger",
  completed: "s-sent",
  skipped: "s-waiting",
};

/** On track says nothing: a pill on every card that is fine is noise. */
export const SLA_LABEL: Record<SlaStatus, string | null> = {
  on_track: null,
  due_soon: "Due soon",
  overdue: "Overdue",
  critical: "Critical",
};

export const SLA_CLASS: Record<SlaStatus, string> = {
  on_track: "",
  due_soon: "s-warn",
  overdue: "s-danger",
  critical: "s-danger",
};

/** Where somebody is in their time here, as the Employees index says it. */
export const EMPLOYEE_STATE_LABEL: Record<EmployeeState, string> = {
  onboarding: "Onboarding",
  active: "Active",
  offboarding: "Offboarding",
  left: "Left",
  withdrawn: "Did not start",
};

export const EMPLOYEE_STATE_CLASS: Record<EmployeeState, string> = {
  onboarding: "s-progress",
  active: "s-sent",
  offboarding: "s-warn",
  left: "s-waiting",
  withdrawn: "s-waiting",
};

/** What to call one instance: an offboarding is never an "onboarding", and somebody staying is never offboarded. */
export const kindWord = (instance: { kind: LifecycleKind; trigger: string }): string =>
  workWord(instance.kind, instance.trigger);

/** The day it counts from, as a label: "Start date", "Last day", "Day access goes". */
export const anchorLabel = (instance: { kind: LifecycleKind; trigger: string }): string =>
  words(anchorText(instance.kind, instance.trigger));

export const ROLE_LABEL: Record<MembershipRole, string> = {
  admin: "Admin",
  hr: "HR",
  manager: "Manager",
  member: "Member",
  compliance: "Compliance",
};

export const ROLES: readonly MembershipRole[] = ["member", "manager", "hr", "admin", "compliance"];

export const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

/** A `YYYY-MM-DD` day as people say it. Read at noon UTC, so no zone can move it to the day before. */
export function dayText(iso: string, withYear = false): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    ...(withYear ? { year: "numeric" } : {}),
    timeZone: "UTC",
  });
}

/** An instant, in the viewer's own zone. */
export function whenText(ms: number): string {
  return new Date(ms).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * An instant on a site's clock, with its zone named: "Fri, 10 Oct, 17:00 EDT".
 * For the access cut-off, where "5pm" has to mean 5pm where the badge reader is.
 */
export function siteTimeText(ms: number, timeZone: string): string {
  return new Date(ms).toLocaleString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    timeZone,
    timeZoneName: "short",
  });
}

/**
 * Access gone against the time it was asked for (`accessTimes()`), as a
 * clause: "12 min after it was asked for". Not done and not due yet is null;
 * not done and past the time says how far past.
 */
export function accessText(
  requestedAt: number,
  doneAt: number | null,
  now: number,
): { text: string; late: boolean } | null {
  const at = doneAt ?? now;
  if (doneAt === null && at < requestedAt) return null;
  const off = at - requestedAt;
  if (Math.abs(off) < 60_000) return { text: "at the time it was asked for", late: false };
  if (off < 0) return { text: `${durationText(-off)} before the time asked for`, late: false };
  return {
    text: `${doneAt === null ? "still not done " : ""}${durationText(off)} after it was asked for`,
    late: true,
  };
}

/** When a card is due: its day, or for an SLA counted in minutes, the moment. */
export function dueText(card: Pick<CardFull, "dueDay" | "dueAt">): string {
  return card.dueDay ? `Due ${dayText(card.dueDay)}` : `Due by ${whenText(card.dueAt)}`;
}

/** "end-of-contract" as a person would write it. */
export function words(value: string): string {
  return value.replace(/-/g, " ").replace(/^./, (first) => first.toUpperCase());
}

export const plural = (count: number, one: string, many = `${one}s`): string =>
  `${count} ${count === 1 ? one : many}`;

/** A length of time as people say it: "45 min", "2 h 10 min", "3 days 4 h". */
export function durationText(ms: number): string {
  const minutes = Math.max(0, Math.round(ms / 60_000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return minutes % 60 ? `${hours} h ${minutes % 60} min` : `${hours} h`;
  const days = Math.floor(hours / 24);
  return hours % 24 ? `${days} days ${hours % 24} h` : `${days} days`;
}

const SMALL = ["No", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten"];

/** My work's headline: how many cards need this person now, as a sentence. */
export function waitingText(count: number): string {
  if (count <= 0) return "Nothing is waiting on you";
  if (count === 1) return "One thing is waiting on you";
  return `${SMALL[count] ?? count} things are waiting on you`;
}

/** Today, as the top of My work says it: "Thursday 29 October". */
export function todayText(now: Date): string {
  return now.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });
}

/** A recent instant as a weekday ("Tue"), or a date once it is a week or more ago. */
export function recentText(ms: number, now: number): string {
  return new Date(ms).toLocaleDateString(
    undefined,
    now - ms < 6 * 86_400_000 ? { weekday: "short" } : { day: "numeric", month: "short" },
  );
}

/** "a", "a and b", "a, b and c". */
export function listText(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}
